(function () {
  'use strict';

  function countryCode(value) {
    if (typeof value !== 'string') return null;

    var code = value.trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : null;
  }

  function languageCode(value) {
    if (typeof value !== 'string') return null;

    var code = value.trim().replace(/_/g, '-').toLowerCase();
    return /^[a-z]{2,8}(?:-[a-z0-9]{1,8})*$/.test(code) ? code : null;
  }

  function findCountry(countries, code) {
    var normalized = countryCode(code);
    if (!normalized || !Array.isArray(countries)) return null;

    return countries.find(function (country) {
      return country && countryCode(country.code) === normalized;
    }) || null;
  }

  function languageCodes(country) {
    if (!country || !Array.isArray(country.languages)) return [];

    return country.languages.map(function (language) {
      return language && languageCode(language.code);
    }).filter(Boolean);
  }

  function selectableLanguageCodes(country) {
    if (!country || !Array.isArray(country.selectableLanguages)) return [];

    var published = languageCodes(country);
    return country.selectableLanguages.map(languageCode).filter(function (code) {
      return code && published.indexOf(code) !== -1;
    });
  }

  function matchLanguage(requested, supported) {
    var normalized = languageCode(requested);
    if (!normalized) return null;
    if (supported.indexOf(normalized) !== -1) return normalized;

    var base = normalized.split('-')[0];
    if (supported.indexOf(base) !== -1) return base;

    return supported.find(function (code) {
      return code.split('-')[0] === base;
    }) || null;
  }

  function chooseSupportedLanguage(supported, browserLanguages, suggestedLanguage, currentLanguage) {
    var preferred = Array.isArray(browserLanguages) ? browserLanguages : [];

    for (var index = 0; index < preferred.length; index += 1) {
      var browserMatch = matchLanguage(preferred[index], supported);
      if (browserMatch) return browserMatch;
    }

    // Use the destination's first eligible language when none of the preferences match.
    return matchLanguage(suggestedLanguage, supported)
      || matchLanguage(currentLanguage, supported)
      || supported[0]
      || null;
  }

  function chooseLanguage(country, browserLanguages, suggestedLanguage, currentLanguage) {
    return chooseSupportedLanguage(languageCodes(country), browserLanguages, suggestedLanguage, currentLanguage);
  }

  function sameExperience(current, targetCountry, targetLanguage) {
    if (!current || !targetCountry) return false;

    var currentLanguage = languageCode(current.language);
    var targetCode = languageCode(targetLanguage);
    if (!currentLanguage || currentLanguage !== targetCode) return false;
    if (!current.currency || current.currency !== targetCountry.currency) return false;

    var currentCountry = countryCode(current.country);
    if (currentCountry && currentCountry === countryCode(targetCountry.code)) return true;

    // Countries can share a market, currency and language without offering a better experience.
    return typeof current.market === 'string'
      && current.market.length > 0
      && current.market === targetCountry.market;
  }

  function suggestionParts(suggestion) {
    return suggestion && typeof suggestion.parts === 'object' && suggestion.parts !== null
      ? suggestion.parts
      : {};
  }

  function getRecommendation(current, countries, response, browserLanguages) {
    if (!current || !response || typeof response !== 'object' || Array.isArray(response)) return null;

    var currentCountry = findCountry(countries, current.country);
    if (!currentCountry || !languageCode(current.language)) return null;

    var detected = response.detected_values && response.detected_values.country;
    if (detected && !findCountry(countries, detected.handle)) return null;
    if (!Array.isArray(response.suggestions)) return null;

    var targetCountry = currentCountry;
    var suggestedLanguage = null;

    for (var index = 0; index < response.suggestions.length; index += 1) {
      var parts = suggestionParts(response.suggestions[index]);
      var suggestedCountry = parts.country && findCountry(countries, parts.country.handle);

      if (suggestedCountry) {
        targetCountry = suggestedCountry;
        suggestedLanguage = parts.language && parts.language.handle;
        break;
      }

      if (!parts.country && !suggestedLanguage && parts.language) {
        suggestedLanguage = parts.language.handle;
      }
    }

    var targetLanguage = chooseSupportedLanguage(
      selectableLanguageCodes(targetCountry), browserLanguages, suggestedLanguage, current.language
    );
    if (!targetLanguage || sameExperience(current, targetCountry, targetLanguage)) return null;

    return { country: countryCode(targetCountry.code), language: targetLanguage };
  }

  function validChoice(choice, countries) {
    if (!choice || typeof choice !== 'object' || Array.isArray(choice)) return false;

    var country = findCountry(countries, choice.country);
    var language = languageCode(choice.language);
    return Boolean(country && language && selectableLanguageCodes(country).indexOf(language) !== -1);
  }

  function manualChoices(current, countries) {
    if (!Array.isArray(countries)) return [];

    var groups = [];
    countries.forEach(function (country) {
      if (!country || !countryCode(country.code) || !country.market || !country.currency) return;

      selectableLanguageCodes(country).forEach(function (code) {
        var group = groups.find(function (choice) {
          return choice.market === country.market && choice.currency === country.currency && choice.language === code;
        });
        if (!group) {
          var language = country.languages.find(function (published) {
            return published && languageCode(published.code) === code;
          });
          group = {
            country: null,
            language: code,
            market: country.market,
            currency: country.currency,
            name: language.name,
            countries: []
          };
          groups.push(group);
        }

        var codeForCountry = countryCode(country.code);
        if (group.countries.indexOf(codeForCountry) === -1) group.countries.push(codeForCountry);
      });
    });

    var currentCountry = current && countryCode(current.country);
    groups.forEach(function (group) {
      // Keep an existing country, but let Shopify resolve a different multi-country market.
      if (currentCountry && group.countries.indexOf(currentCountry) !== -1) group.country = currentCountry;
      else if (group.countries.length === 1) group.country = group.countries[0];
    });
    return groups;
  }

  function preferenceState(raw, now, countries) {
    if (typeof raw !== 'string' || !Number.isFinite(now)) return null;

    var record;
    try {
      record = JSON.parse(raw);
    } catch (error) {
      return null;
    }

    if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
    if (!Number.isFinite(record.expiresAt) || record.expiresAt <= now) return null;

    if (record.kind === 'dismissed') {
      return { kind: 'dismissed', expiresAt: record.expiresAt };
    }

    if (record.kind === 'pending' && !Object.prototype.hasOwnProperty.call(record, 'country')) {
      var language = languageCode(record.language);
      var group = manualChoices(null, countries).find(function (choice) {
        return choice.market === record.market && choice.currency === record.currency && choice.language === language;
      });
      if (!group) return null;

      return {
        kind: 'pending',
        market: group.market,
        language: group.language,
        currency: group.currency,
        expiresAt: record.expiresAt
      };
    }

    if ((record.kind !== 'selected' && record.kind !== 'pending') || !validChoice(record, countries)) return null;

    return {
      kind: record.kind,
      country: countryCode(record.country),
      language: languageCode(record.language),
      expiresAt: record.expiresAt
    };
  }

  window.BarkLocalizationPolicy = {
    chooseLanguage: chooseLanguage,
    getRecommendation: getRecommendation,
    validChoice: validChoice,
    manualChoices: manualChoices,
    sameExperience: sameExperience,
    preferenceState: preferenceState
  };
}());
